import { Application } from '../application/application';
import { XHRDataSource } from '../data-source/xhr-source';
import { MapOverlay, MapOverlayDatumContentProducer, MapItemBounds, MapDataCluster, MapLayout, MapOverlayCategory } from '../map/map-overlay';
import { Visualizer, VisualizerControl, VisualizationUpdateLevel } from '../data-source/visualizer';
import { AnimationMode } from '../data-source/types';
import { GoiDatum, goiDataReader } from '../poi/poi-common'
import { getPaletteColor, adjustColor, colorToString } from '../util/color';
import { Pixel } from '../map/map-types';
import { Size } from '../data/common';
import { Timeline } from '../timeline/timeline';
import { TransverseMercator, Mercator } from '../map/projection';
import { Geodetic, CartesianBounds, Coordinate, Cartesian } from '../map/map-types';
import { SVGRoot, ViewBox, Color } from '../svg/svg';
import { MultiPointGeometry } from './modeling-common';

export class ImpactMonitorVisualizerControl implements VisualizerControl {

	private containers: Set<string>;

	public constructor(private application: Application, private visualizer: Visualizer) {
		this.containers = new Set<string>();
	}

	public addContainerId(containerId: string) {
		this.getContainerIds().add(containerId);
		this.visualizer.refresh(VisualizationUpdateLevel.settings);
	}
	public removeContainerId(containerId: string) {
		this.getContainerIds().delete(containerId);
		this.visualizer.refresh(VisualizationUpdateLevel.settings);
	}

	protected getContainerIds(): Set<string> {
		return this.containers;
	}

	public enabled(visualizerKey: string): boolean {
		if (visualizerKey) {
			if (visualizerKey.startsWith('monitors')) {
				let keyParts = visualizerKey.split("_");
				let containerId = keyParts[1];
				if (containerId) {
					let enabler = <HTMLInputElement>this.application.getInspectionPanel().getContainer(containerId).getContainerNode().querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-enabler input`);

					if (enabler) {
						this.containers.add(containerId);
						return enabler.checked;
					} else {
						this.containers.delete(containerId);
						console.warn(`Visualizer key ${visualizerKey} could not be found in settings section for container ${containerId}`);
					}
				} else {
					return [...this.containers].reduce((enabled, containerId) => {
						if (!enabled) {
							//							let containerNode = this.application.getInspectionPanel().getContainer(containerId).getContainerNode();
							//							let enabler = <HTMLInputElement>containerNode.querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.monitors_${containerId} .tlmd-enabler input`);

							let enabler = <HTMLInputElement>this.application.getInspectionPanel().getContainer(containerId).getContainerNode().querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.monitors_${containerId} .tlmd-enabler input`);
							if (enabler) {
								return enabler.checked;
							} else {
								this.containers.delete(containerId);
								console.warn(`Visualizer key monitors_${containerId} could not be found in settings section for container ${containerId}`);
								return false;
							}
						}
						return true
					}, false);
				}
			}
		} else {
			return [...this.containers].reduce((enabled, containerId) => {
				if (!enabled) {
					let enabler = <HTMLInputElement>this.application.getInspectionPanel().getContainer(containerId).getContainerNode().querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.monitors_${containerId} .tlmd-enabler input`);
					if (enabler) {
						return enabler.checked;
					} else {
						this.containers.delete(containerId);
						console.warn(`Visualizer key monitors_${containerId} could not be found in settings section for container ${containerId}`);
						return false;
					}
				}
				return true
			}, false);
		}
	}
	public setEnabled(visualizerKey: string, enabled: boolean): void {
		if (visualizerKey) {
			if (visualizerKey.startsWith('monitors')) {
				let keyParts = visualizerKey.split("_");
				let containerId = keyParts[1];
				if (containerId) {
					let enabler = <HTMLInputElement>this.application.getInspectionPanel().getContainer(containerId).getContainerNode().querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-enabler input`);
					if (enabler) {
						this.containers.add(containerId);
						if (enabler.checked != enabled) {
							enabler.checked = enabled;
							this.enableVisualizer(visualizerKey, enabled);
						}
					} else {
						this.containers.delete(containerId);
						console.warn(`Visualizer key ${visualizerKey} could not be found in settings section for container ${containerId}`);
					}
				}
			}
		} else {
			let needsRefreshing = [...this.containers].reduce((refresh, containerId) => {
				let enabler = <HTMLInputElement>this.application.getInspectionPanel().getContainer(containerId).getContainerNode().querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.monitors_${containerId} .tlmd-enabler input`);
				if (enabler) {
					if (enabler.checked != enabled) {
						enabler.checked = enabled;
						return true;
					}
				} else {
					this.containers.delete(containerId);
					console.warn(`Visualizer key monitors_${containerId} could not be found in settings section for container ${containerId}`);
				}
				return false;
			}, false);

			if (needsRefreshing) {
				this.visualizer.refresh(VisualizationUpdateLevel.settings);
			}
		}
	}
	public animationMode(visualizerKey: string): AnimationMode {
		return AnimationMode.under;
	}

	public enableVisualizer(visualizerKey: string, enable: boolean): void {
		if (visualizerKey.startsWith('monitors')) {
			let keyParts = visualizerKey.split("_");
			let containerId = keyParts[1];
			if (containerId) {
				let enabler = <HTMLInputElement>this.application.getInspectionPanel().getContainer(containerId).getContainerNode().querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-enabler input`);
				if (enabler) {
					this.containers.add(containerId);
					this.visualizer.refresh(VisualizationUpdateLevel.settings);
				} else {
					console.warn(`Visualizer key ${visualizerKey} could not be found in settings section for container ${containerId}`);
					this.containers.delete(containerId);
				}
			}
		}
	}
}

export class ImpactMonitorMapVisualizer extends MapOverlay implements Visualizer {

	private controls: { [key: string]: ImpactMonitorVisualizerControl }

	public constructor(private visualizerKey: string, private source: XHRDataSource, className: string) {
		super(
			MapOverlayCategory.BOTTOM,
			className,
			{
				eventHandlers: {
					'click': (evAndTrgt, _data) => {
						evAndTrgt.event.preventDefault();
						evAndTrgt.event.stopPropagation();

						let overlayItem = (<MapOverlayDatumContentProducer>this.getProducer()).getItem(evAndTrgt.target.id);
						if (overlayItem) {
							source.getApplication().getInspectionPanel().switchContainer(overlayItem.group).then(_containerPanel => {
								source.onSelection(overlayItem.group, overlayItem.item.dataItemIds);
							});
						}
					}
				},
				elementData: {
					'source': source.getName(),
					'visualizer': visualizerKey
				}
			},
			new MapOverlayDatumContentProducer(
				{
					size: () => { return { width: 20, height: 20, border: 2 }; },
					draw: (domElem, meta, size: Size, _localProjection) => {
						// dataItem0 is first datum in cluster 
						let dataItem0 = <GoiDatum>meta.dataItems[meta.dataItemIds[0]];
						
						const title = dataItem0.name;
						const bgColor = dataItem0.color;

						let baseColor = getPaletteColor(0, 0);
						domElem.title = title;

						if (dataItem0.geometry.lat) {
							//a location
							domElem.style.color = bgColor ?? colorToString(baseColor);
							domElem.style.lineHeight = `${meta.widthPX}px`;
							domElem.style.fontSize = `${meta.widthPX}px`;
							domElem.classList.add('icon-location');


							// add div for the number of underlying observations when count greater than 1
							if (meta.count > 1) {
								domElem.innerHTML = `<div class="tlmd-marker-label"><div>${meta.count}</div></div>`
							}
						} else {
							//an area
							domElem.innerHTML = createAreaOverlayItem((<MultiPointGeometry>dataItem0.geometry).coordinates,bgColor ? bgColor : adjustColor(baseColor, { hue: 0, saturation: 0, lightness: 0, alpha: -0.5 }),dataItem0.isHole);
						}
					},
					overlap: (item0: MapItemBounds, item1: MapItemBounds): boolean => {
						//this renderer assumes circles
						let c0x = item0.lngPX;
						let c0y = item0.latPX;

						let c1x = item1.lngPX
						let c1y = item1.latPX;


						let dx = c0x - c1x;
						let dy = c0y - c1y;
						let dPOW2 = dx * dx + dy * dy;
						let r0 = item0.widthPX / 2;
						let r1 = item1.widthPX / 2;
						let r0PLUSr1 = r0 + r1;

						return (dPOW2 < r0PLUSr1 * r0PLUSr1);
					},
					merge: (clusters: MapDataCluster[], minimimSize: Size): MapDataCluster => {
						if (clusters && clusters.length > 0) {
							if (clusters.length > 1) {
								let growingCluster = clusters[0];
								let clusterBounds = growingCluster.bounds.reduce((bounds, mpItemBounds) => {
									bounds.ne.x = Math.max(bounds.ne.x, mpItemBounds.eastPX);
									bounds.ne.y = Math.min(bounds.ne.y, mpItemBounds.northPX);
									bounds.sw.x = Math.min(bounds.sw.x, mpItemBounds.westPX);
									bounds.sw.y = Math.max(bounds.sw.y, mpItemBounds.southPX);
									return bounds;
								}, {
									ne: <Pixel>{ x: growingCluster.eastPX, y: growingCluster.northPX },
									sw: <Pixel>{ x: growingCluster.westPX, y: growingCluster.southPX }
								});

								let width = clusterBounds.ne.x - clusterBounds.sw.x;
								let height = clusterBounds.sw.y - clusterBounds.ne.y;

								growingCluster.lngPX = clusterBounds.sw.x + width / 2;
								growingCluster.latPX = clusterBounds.ne.y + height / 2;

								let size = Math.max(minimimSize.width, width, height);
								growingCluster.widthPX = size;
								growingCluster.heightPX = size;
								growingCluster.northPX = growingCluster.latPX - size / 2;
								growingCluster.southPX = growingCluster.northPX + size;
								growingCluster.westPX = growingCluster.lngPX - size / 2;
								growingCluster.eastPX = growingCluster.westPX + size;

								growingCluster.east = undefined;
								growingCluster.west = undefined;
								growingCluster.north = undefined;
								growingCluster.south = undefined;
								growingCluster.lat = undefined;
								growingCluster.lng = undefined;

								return growingCluster;

							} else {
								return clusters[0];
							}
						} else {
							return undefined;
						}

					}

				},
				goiDataReader,
				{
					layout: MapLayout.overlap
				})
		);
	}

	public key(): string {
		return this.visualizerKey;
	}

	public type(): string {
		//this visualizer is multi type
		return undefined;
	}

	public setControls(controls: { [key: string]: VisualizerControl }): Visualizer {
		this.controls = <{ [key: string]: ImpactMonitorVisualizerControl }>controls;
		return this;
	}

	public release(): void {
	}

	public setTimeline(_timeline: Timeline): void {
		//we do not deal with the timeline here
	}

	public updateSelection(): void {
		//not implemented (yet??)
	}


	public refresh(_level: VisualizationUpdateLevel): void {
		if (this.controls) {
			//console.log('refreshing ImpactMonitorMapVisualizer @ level %s',_level);

			(<MapOverlayDatumContentProducer>this.getProducer()).setAnimationPosition(this.source.getTimeline().getCursorMillis(true));

			let typesAndModes = Object.entries(this.controls)
				.filter(controlEntry => controlEntry[1].enabled(this.key()))
				.reduce((accu, control) => {
					accu.types.push(control[0]);
					accu.modes[control[0]] = control[1].animationMode(this.key());
					return accu;
				}, <{ types: string[], modes: { [key: string]: AnimationMode } }>{ types: [], modes: {} })



			if (_level <= VisualizationUpdateLevel.filter) {
				let dataMap = this.source.getData(typesAndModes.types);
				(<MapOverlayDatumContentProducer>this.getProducer()).setData(dataMap);
			}

			(<MapOverlayDatumContentProducer>this.getProducer()).setAnimationModes(typesAndModes.modes);

			this.update();


		}
	}

}

const createAreaOverlayItem = (coordinates: Geodetic[], color: Color|string, drawContour: boolean): string => {
	const EARTH_RADIUS = TransverseMercator.getDefaultDatum().radius();
	const EARTH_CIRCUMFERENCE = EARTH_RADIUS * 2 * Math.PI;
	const MAP_RESOLUTION = EARTH_CIRCUMFERENCE;

	const points = coordinates.map(coordinate => Mercator.fromLatLngToPointArray(coordinate, MAP_RESOLUTION));
	const bounds = points.reduce((bounds, p) => {
		bounds.southWest.x = bounds.southWest.x === undefined ? p[0] : Math.min(bounds.southWest.x, p[0]);
		bounds.southWest.y = bounds.southWest.y === undefined ? p[1] : Math.min(bounds.southWest.y, p[1]);
		bounds.northEast.x = bounds.northEast.x === undefined ? p[0] : Math.max(bounds.northEast.x, p[0]);
		bounds.northEast.y = bounds.northEast.y === undefined ? p[1] : Math.max(bounds.northEast.y, p[1]);
		return bounds;
	}, <CartesianBounds>{ southWest: { x: undefined, y: undefined }, northEast: { x: undefined, y: undefined } });

	const width = bounds.northEast.x - bounds.southWest.x;
	const height = bounds.northEast.y - bounds.southWest.y;

	const transform = (coord: Coordinate): number[] => [coord[0] - bounds.southWest.x, height - coord[1] + bounds.southWest.y];

	const straightPath = (transform: (p: number[]) => number[], lineString: Coordinate[]) => lineString.flatMap((point, index) => [index === 0 ? 'M' : 'L', ...transform(point)]);


	let path: (string | number)[] = straightPath(transform, points);

	const svgPolygonPath = (path: string, color: string, className?: string) => drawContour 
	? `<path ${className ? `class="${className}" ` : ''} d="${path}" fill="none" stroke="${color}" stroke-width="4" />`
	: `<path ${className ? `class="${className}" ` : ''}fill-rule="evenodd" d="${path}" fill="${color}" stroke="none" />`;

	const svgPath = svgPolygonPath(path.join(' '), (typeof color === "string" ? <string>color : colorToString(<Color>color)), 'tlmd-monitor');
//	const svgPath = svgPolygonPath(path.join(' '), colorToString(color), 'tlmd-monitor');

	const viewBox: ViewBox = {
		minX: 0,
		minY: 0,
		width: width,
		height: height
	}

	const svgRoot = {
		content: svgPath,
		viewBox: viewBox
	}
	const wrapInSVG = (svgRoot: SVGRoot) => `<svg viewBox="${svgRoot.viewBox.minX} ${svgRoot.viewBox.minY} ${svgRoot.viewBox.width} ${svgRoot.viewBox.height}" preserveAspectRatio="xMinYMin slice" style="width:100%; height:100%; position:absolute;">${svgRoot.content}</svg>`;

	let svg = wrapInSVG(svgRoot);

	return svg;
}
